// Helper functions for image segmentation data conversion

export interface Point {
  x: number;
  y: number;
}

export interface Polygon {
  points: Point[];
  label: string;
  color: string;
}

export interface Label {
  name: string;
  color: string;
}

export interface SegmentationAnnotationPayload {
  image_id: string;
  segments: number[][];
  class_ids: number[];
  size: number[];
  target_size: number[];
}

interface SegmentationData {
  [imageId: string]: {
    polygons: Polygon[];
    activePolygon: Point[];
  };
}

export const segmentsToPolygons = (
  segments: number[][] | undefined,
  classIds: number[] | undefined,
  labels: Label[]
): Polygon[] => {
  if (!segments?.length) return [];

  const count = Math.min(
    segments.length,
    classIds?.length ?? segments.length
  );

  if (classIds && classIds.length !== segments.length) {
    console.warn(
      'segmentsToPolygons: segments and class_ids length mismatch',
      { segments: segments.length, classIds: classIds.length }
    );
  }

  const polygons: Polygon[] = [];

  for (let i = 0; i < count; i++) {
    const flatCoords = segments[i];
    if (!flatCoords?.length || flatCoords.length < 6) continue;

    const points: Point[] = [];
    for (let j = 0; j < flatCoords.length - 1; j += 2) {
      points.push({ x: flatCoords[j], y: flatCoords[j + 1] });
    }

    const classId = classIds?.[i] ?? 0;
    const labelEntry = labels[classId];

    polygons.push({
      points,
      label: labelEntry?.name ?? '',
      color: labelEntry?.color ?? '#FF0000',
    });
  }

  return polygons;
};

export const getPolygonBounds = (polygons: Polygon[]) => {
  const xs = polygons.flatMap((p) => p.points.map((pt) => pt.x));
  const ys = polygons.flatMap((p) => p.points.map((pt) => pt.y));
  return {
    maxX: xs.length ? Math.max(...xs) : 0,
    maxY: ys.length ? Math.max(...ys) : 0,
  };
};

/** Distinct color for SAM preview masks before a label is assigned */
export const SAM_UNLABELED_COLOR = '#7B61FF';

export const isUnlabeledPolygon = (polygon: Polygon): boolean => !polygon.label;

export const segmentsToSamPreviewPolygons = (
  segments: number[][] | undefined
): Polygon[] => {
  if (!segments?.length) return [];

  const polygons: Polygon[] = [];

  for (const flatCoords of segments) {
    if (!flatCoords?.length || flatCoords.length < 6) continue;

    const points: Point[] = [];
    for (let j = 0; j < flatCoords.length - 1; j += 2) {
      points.push({ x: flatCoords[j], y: flatCoords[j + 1] });
    }

    polygons.push({
      points,
      label: '',
      color: SAM_UNLABELED_COLOR,
    });
  }

  return polygons;
};

/** How API segment coordinates relate to image pixel space (`size`). */
export type CoordRemapMode = 'none' | 'target' | 'transposed';

const getRemapScales = (
  mode: CoordRemapMode,
  size: number[],
  targetSize: number[],
  direction: 'toImage' | 'toApi'
): { scaleX: number; scaleY: number } => {
  const [sw, sh] = size;
  const [tw, th] = targetSize;

  switch (mode) {
    case 'target':
      return direction === 'toImage'
        ? { scaleX: sw / tw, scaleY: sh / th }
        : { scaleX: tw / sw, scaleY: th / sh };
    case 'transposed':
      return direction === 'toImage'
        ? { scaleX: sw / sh, scaleY: sh / sw }
        : { scaleX: sh / sw, scaleY: sw / sh };
    default:
      return { scaleX: 1, scaleY: 1 };
  }
};

/**
 * Decide remap mode from API `size` / `target_size`, optionally using raw segment bounds.
 * When there are no segments, mode is inferred from dimensions so new drawings save correctly.
 */
export const resolveCoordRemapMode = (
  size: number[],
  targetSize: number[],
  apiBounds?: { maxX: number; maxY: number }
): CoordRemapMode => {
  if (size.length < 2 || targetSize.length < 2) return 'none';

  const [sw, sh] = size;
  const [tw, th] = targetSize;
  if (sw <= 0 || sh <= 0) return 'none';

  const hasApiCoords =
    apiBounds !== undefined && (apiBounds.maxX > 0 || apiBounds.maxY > 0);

  if (hasApiCoords) {
    const { maxX, maxY } = apiBounds!;

    if (maxX <= tw * 1.05 && maxY <= th * 1.05 && (sw !== tw || sh !== th)) {
      return 'target';
    }
    if (maxY > sh * 1.01) {
      return 'transposed';
    }
    return 'none';
  }

  if (sw !== tw || sh !== th) {
    return 'target';
  }
  return 'none';
};

const applyRemapScales = (
  polygons: Polygon[],
  scaleX: number,
  scaleY: number
): Polygon[] => {
  if (scaleX === 1 && scaleY === 1) return polygons;

  return polygons.map((p) => ({
    ...p,
    points: p.points.map((pt) => ({
      x: pt.x * scaleX,
      y: pt.y * scaleY,
    })),
  }));
};

/** Map API segment coordinates to canvas/image pixel space (`size`). */
export const mapPolygonsToImageSpace = (
  polygons: Polygon[],
  size: number[],
  targetSize: number[],
  mode: CoordRemapMode
): Polygon[] => {
  if (!polygons.length || size.length < 2 || targetSize.length < 2) return polygons;

  const [sw, sh] = size;
  if (sw <= 0 || sh <= 0) return polygons;

  const { scaleX, scaleY } = getRemapScales(mode, size, targetSize, 'toImage');
  return applyRemapScales(polygons, scaleX, scaleY);
};

/** Map a single point from canvas/image space to API space */
export const mapPointFromImageSpace = (
  point: Point,
  size: number[],
  targetSize: number[],
  mode: CoordRemapMode
): Point => {
  const [mapped] = mapPolygonsFromImageSpace(
    [{ points: [point], label: '', color: '' }],
    size,
    targetSize,
    mode
  );
  return mapped.points[0];
};

/** Map a single point from API space to canvas/image space */
export const mapPointToImageSpace = (
  point: Point,
  size: number[],
  targetSize: number[],
  mode: CoordRemapMode
): Point => {
  const [mapped] = mapPolygonsToImageSpace(
    [{ points: [point], label: '', color: '' }],
    size,
    targetSize,
    mode
  );
  return mapped.points[0];
};

/** Inverse of mapPolygonsToImageSpace for save. */
export const mapPolygonsFromImageSpace = (
  polygons: Polygon[],
  size: number[],
  targetSize: number[],
  mode: CoordRemapMode
): Polygon[] => {
  if (!polygons.length || size.length < 2 || targetSize.length < 2) return polygons;

  const { scaleX, scaleY } = getRemapScales(mode, size, targetSize, 'toApi');
  return applyRemapScales(polygons, scaleX, scaleY);
};

export const polygonsToAnnotation = (
  polygons: Polygon[],
  labels: Label[],
  imageId: string,
  size: number[],
  targetSize: number[]
): SegmentationAnnotationPayload => {
  const segments = polygons.map((polygon) =>
    polygon.points.flatMap((point) => [point.x, point.y])
  );

  const class_ids = polygons.map((polygon) => {
    const index = labels.findIndex((l) => l.name === polygon.label);
    return index >= 0 ? index : 0;
  });

  return {
    image_id: imageId,
    segments,
    class_ids,
    size,
    target_size: targetSize,
  };
};

/** Parse annotations from GET /data/{projectId}/image/{imageId} response */
export const parseImageApiAnnotations = (
  data: {
    segments?: number[][];
    class_ids?: number[];
    polygons?: Polygon[];
  },
  labels: Label[]
): Polygon[] => {
  if (data.polygons?.length) {
    return data.polygons.map((polygon) => ({
      points: polygon.points || [],
      label: polygon.label || '',
      color: polygon.color || '#FF0000',
    }));
  }

  return segmentsToPolygons(data.segments, data.class_ids, labels);
};

// Convert segmentation data to the format expected by the backend
export const convert_to_image_segmentation_data = (segmentationData: SegmentationData) => {
  const convertedData: { [imageId: string]: Polygon[] } = {};

  Object.entries(segmentationData).forEach(([imageId, data]) => {
    convertedData[imageId] = data.polygons.map(polygon => ({
      points: polygon.points,
      label: polygon.label,
      color: polygon.color
    }));
  });

  return convertedData;
};

// Convert backend data to the format used by the frontend
export const convert_from_backend_data = (backendData: any): SegmentationData => {
  const convertedData: SegmentationData = {};

  if (backendData && typeof backendData === 'object') {
    Object.entries(backendData).forEach(([imageId, data]: [string, any]) => {
      if (Array.isArray(data)) {
        convertedData[imageId] = {
          polygons: data.map((polygon: any) => ({
            points: polygon.points || [],
            label: polygon.label || '',
            color: polygon.color || '#FF0000'
          })),
          activePolygon: []
        };
      }
    });
  }

  return convertedData;
};

// Validate polygon data
export const validatePolygon = (polygon: Polygon): boolean => {
  return (
    polygon.points.length >= 3 &&
    polygon.label.length > 0 &&
    polygon.color.length > 0
  );
};

// Calculate polygon area (for validation or analysis)
export const calculatePolygonArea = (points: Point[]): number => {
  if (points.length < 3) return 0;

  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }

  return Math.abs(area) / 2;
};

// Check if a point is inside a polygon
export const isPointInPolygon = (point: Point, polygon: Point[]): boolean => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    
    const intersect = ((yi > point.y) !== (yj > point.y))
        && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

export const VERTEX_HIT_RADIUS = 5;
export const EDGE_HIT_THRESHOLD = 10;

// Calculate distance between two points
export const calculateDistance = (point1: Point, point2: Point): number => {
  return Math.hypot(point2.x - point1.x, point2.y - point1.y);
};

/** Closest point on segment AB and distance from P to that point */
export const distanceToLineSegment = (
  point: Point,
  lineStart: Point,
  lineEnd: Point
): { distance: number; point: Point } => {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) param = dot / lenSq;

  let xx: number;
  let yy: number;

  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }

  const dx = point.x - xx;
  const dy = point.y - yy;

  return {
    distance: Math.sqrt(dx * dx + dy * dy),
    point: { x: xx, y: yy },
  };
};

/** True when point is within hit distance of a vertex or edge (edit interactions) */
export const isNearPolygonEdgesOrVertices = (
  point: Point,
  polygon: Point[]
): boolean => {
  if (polygon.length < 2) return false;

  if (
    polygon.some(
      (p) => Math.hypot(p.x - point.x, p.y - point.y) < VERTEX_HIT_RADIUS
    )
  ) {
    return true;
  }

  for (let i = 0; i < polygon.length; i++) {
    const { distance } = distanceToLineSegment(
      point,
      polygon[i],
      polygon[(i + 1) % polygon.length]
    );
    if (distance < EDGE_HIT_THRESHOLD) return true;
  }

  return false;
};

// Find the closest point in a polygon to a given point
export const findClosestPoint = (point: Point, polygon: Point[]): { point: Point; index: number; distance: number } => {
  let closestPoint = polygon[0];
  let closestIndex = 0;
  let minDistance = calculateDistance(point, polygon[0]);

  polygon.forEach((polygonPoint, index) => {
    const distance = calculateDistance(point, polygonPoint);
    if (distance < minDistance) {
      minDistance = distance;
      closestPoint = polygonPoint;
      closestIndex = index;
    }
  });

  return { point: closestPoint, index: closestIndex, distance: minDistance };
}; 